<i18n>
{
  "fr": {
    "tool-title": "Distribution au point de coordonnées",
    "no-data-title": "Aucune donnée",
    "altitude-unit": "msm",
    "elevation-label": "Altitude [msm]",
    "percentile-label": "Percentile [%]",
    "probability-label": "Probabilité [%]",
    "conversion-title": "Conversion",
    "elevation-placeholder": "Saisir une altitude"
  },
  "de": {

  }
}
</i18n>

<template>
  <div class="tool" id="gw-percentiles-raster-point" v-if="show">
    <h3>{{$t('tool-title')}}</h3>
    <md-content class="md-scrollbar main-container" v-if="!loading">
      <md-empty-state
        v-if="nodata"
        class="empty-data"
        md-rounded
        md-icon="graphic_eq"
        :md-label="$t('no-data-title')"
        :md-size="170"
      >
      </md-empty-state>
      <div class="data-graph" v-if="!nodata">
        <plotly
          v-if="showGraph"
          :data="graphData"
          :layout="graphLayout"
          ref="plotly"
        />
      </div>

      <div class="data-conversion">
        <h4>{{ $t('conversion-title') }}</h4>
        <div class="input-container">
          <md-field>
            <label>{{ $t('elevation-label') }}</label>
            <md-input v-model="selectedElevation" type="number" step="0.01" :placeholder="$t('elevation-placeholder')"></md-input>
          </md-field>
          <md-field>
            <label>{{ $t('probability-label') }}</label>
            <md-input :value="selectedProbability" readonly></md-input>
          </md-field>
        </div>
      </div>
    </md-content>
    <div class="loading" v-if="loading">
      <md-progress-spinner class="loading-animation" :md-diameter="40" :md-stroke="5" md-mode="indeterminate"></md-progress-spinner>
    </div>
  </div>
</template>

<script>

import _ from 'lodash'

import MapStoreMixin from '@/mixins/store/map'
import LayerStoreMixin from '@/mixins/store/layer'

import Layer from '@/components/vue-map/layers/layer'
import SourceProxy from '@/proxies/source'
import plotly from '@/components/common/plotly'
import { PlotlyData, PlotlyXAxis, PlotlyYAxis, PlotlyLayout } from '@/utils/plotly'
import MonotonicCubicSpline from '@/utils/monotonicCubicSpline'
const NODATA_VALUES = [-3.4028231e+38]

export default {
  name: 'GwPercentilesRasterPoint',
  mixins: [
    MapStoreMixin,
    LayerStoreMixin
  ],
  components: {
    plotly
  },
  props: {
    params: {
      type: Object,
      default: () => { return {} }
    }
  },
  computed: {
    show () {
      return this.currentGeom !== null && this.currentGeom.type === 'Point'
    },
    showGraph () {
      return !this.loading && !_.isEmpty(this.graphData) && !this.nodata
    },
    nodata () {
      return this.data === null
    },
    layer () {
      return this.selectedLayers[0]
    },
    currentDepth () {
      return this.layer.getSource().getParams().depth
    },
    graphLayout () {
      let toReturn = new PlotlyLayout()
      toReturn.xaxis = new PlotlyXAxis(this.$t(`elevation-label`))
      toReturn.xaxis.fixedrange = true
      toReturn.xaxis.range = [this.data.Q5.toFixed(1) - 0.2, this.data.Q98.toFixed(1) + 0.2]
      toReturn.xaxis.dtick = 0.5
      toReturn.xaxis.tickangle = '90'

      toReturn.yaxis = new PlotlyYAxis(this.$t(`percentile-label`))
      toReturn.yaxis.range = [0, 100]
      toReturn.yaxis.fixedrange = true
      toReturn.yaxis.tickmode = 'array'
      toReturn.yaxis.tickvals = _.range(0, 105, 5)
      toReturn.yaxis.ticktext = toReturn.yaxis.tickvals.map((val) => val % 25 === 0 ? val : '')

      toReturn.margin = {t: 30, b: 60, r: 10, l: 55}

      toReturn.displayHoverLine()
      toReturn.hovermode = 'y'

      if (typeof this.hoverRect !== 'undefined') toReturn.shapes.push(this.hoverRect)
      if (typeof this.hoverLine !== 'undefined') toReturn.shapes.push(this.hoverLine)

      return toReturn
    },
    hoverRect () {
      if (this.selectedPercentile === '-') return undefined
      return {
        layer: 'below',
        type: 'rect',
        xref: 'paper',
        yref: 'y',
        x0: 0,
        y0: this.selectedPercentile,
        x1: 1,
        y1: 100,
        line: {
          width: 0
        },
        fillcolor: 'rgba(68, 138, 255, 0.2)'
      }
    },
    hoverLine () {
      if (this.selectedPercentile === '-') return undefined
      return {
        type: 'line',
        xref: 'paper',
        yref: 'y',
        x0: 0,
        y0: this.selectedPercentile,
        x1: 1,
        y1: this.selectedPercentile,
        line: {
          dash: 'dot',
          color: '#448aff',
          width: 1
        }
      }
    },
    values () {
      return {
        xData: [this.data.Q5, this.data.Q25, this.data.Q50, this.data.Q75, this.data.Q95, this.data.Q98],
        yData: [5, 25, 50, 75, 95, 98]
      }
    },
    interpolatedValues () {
      let xData = []
      let yData = []
      let monotinicCubicSpline = new MonotonicCubicSpline([5, 25, 50, 75, 95, 98], [this.data.Q5, this.data.Q25, this.data.Q50, this.data.Q75, this.data.Q95, this.data.Q98])
      for (let y = 5; y <= 98; y++) {
        xData.push(monotinicCubicSpline.interpolate(y))
        yData.push(y)
      }
      return {
        xData,
        yData
      }
    },
    graphData () {
      let toReturn = new PlotlyData(this.$t('elevation-label'), this.values.xData, this.values.yData, null, '#e72727')
      let unit = this.$t(`altitude-unit`)
      toReturn.hovertemplate = `%{x:.2f} <extra>[${unit}]</extra>`
      toReturn.hoverinfo = 'y'
      toReturn.showlegend = false

      toReturn.mode = 'markers'

      let interpolatedValues = new PlotlyData(this.$t('elevation-label'), this.interpolatedValues.xData, this.interpolatedValues.yData, null, '#e72727')
      interpolatedValues.showlegend = false
      interpolatedValues.line.width = 2
      interpolatedValues.hoverinfo = 'skip'

      return [interpolatedValues, toReturn]
    },
    closestElevation () {
      return this.interpolatedValues.xData.reduce((best, current) => {
        return (current >= this.selectedElevation && (!best || current < best))
          ? current
          : best;
      }, undefined)
    },
    selectedPercentile () {
      if (this.selectedElevation === '' || this.selectedElevation < _.head(this.interpolatedValues.xData) || this.selectedElevation > _.last(this.interpolatedValues.xData)) return '-'
      return this.interpolatedValues.yData[this.interpolatedValues.xData.indexOf(this.closestElevation)]
    },
    selectedProbability () {
      if (this.selectedPercentile === '-') return '-'
      return 100 - this.selectedPercentile
    }
  },
  watch: {
    currentGeom () {
      this.loaded = false
      this.updateTool()
    },
    selectedElevation () {
      this.refreshGraph()
    }
  },
  data() {
    return {
      loading: false,
      loaded: false,
      raster: null,
      topographyRatster: null,
      data: null,
      topographyData: null,
      selectedElevation: ''
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.updateTool()
    })
  },
  beforeDestroy() {
    this.removeTool()
    this.clearHoverFeature()
  },
  methods: {
    updateTool () {
      if (this.loading) return
      if (this.loaded) return this.loading = false
      if (this.currentGeom !== null && this.currentGeom.type === 'Point') this.load()
      else this.removeTool()
    },
    removeTool () {
      if (this.currentGeom !== null && this.currentGeom.type === 'Point') this.clearHoverFeature()
    },
    load () {
      this.loaded = false
      this.loading = true
      this.raster = null
      this.data = null
      this.selectedElevation = ''
      this.setHoverGeoJSON(this.currentGeom)
      this.setDefaultHoverStyle()

      let toFetch = [
        this.fetchRaster(this.params.sourceToFetch),
        this.fetchRaster(this.params.topography)
      ]

      Promise.all(toFetch).then((data) => {
        this.raster = data[0]
        this.topographyRatster = data[1]

        this.fetchData().then(() => {
          this.selectedElevation = this.topographyData.elevation + parseFloat(this.currentDepth)
          this.selectedElevation = this.selectedElevation.toFixed(2)
          // this.selectedElevation = parseFloat(this.currentDepth)
          this.loading = false
          this.loaded = true
        }).catch(this.handleError)
      }).catch(this.handleError)
    },
    fetchRaster (sourceId) {
      return new Promise((resolve, reject) => {
        SourceProxy.getSource(sourceId).then((source) => {
          resolve(Layer.fromSource(source))
        }).catch(reject)
      })
    },
    fetchData () {
      return new Promise((resolve, reject) => {
        this.data = null
        let params = []
        params['INFO_FORMAT'] = 'json'

        let toFetch = [
          this.raster.getFromGeom(this.mapRef, this.currentGeom, params),
          this.topographyRatster.getFromGeom(this.mapRef, this.currentGeom, params)
        ]

        Promise.all(toFetch).then((data) => {
          this.data = data[0]
          this.topographyData = data[1]
          if (NODATA_VALUES.includes(this.data.Q5)) this.data = null
          if (NODATA_VALUES.includes(this.topographyData.elevation)) this.topographyData = null
          resolve()
        }).catch(reject)
      })
    },
    refreshGraph () {
      this.$nextTick(() => {
        if (typeof this.$refs.plotly !== 'undefined') {
          this.$refs.plotly.react()
          this.$refs.plotly.relayout()
        }
      })
    }
  }
}

</script>

<style lang="scss">
 #gw-percentiles-raster-point {
    .empty-data {
      margin-top: 15px;
      margin-bottom: 15px;

      .md-icon {
        width: 80px;
        min-width: 80px;
        font-size: 80px !important;
      }
      .md-empty-state-label {
        font-size: 18px;
        line-height: 18px;
      }

      &.md-empty-state-leave-active {
        display: none !important;
      }
    }

    .data-graph {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 280px;

      & .plotly {
        height: 270px;
        width: 100%;
      }
    }

    .data-conversion {
      .input-container {
        display: flex;

        .md-field {
          max-width: 50%;

          input::-webkit-outer-spin-button, input::-webkit-inner-spin-button, input[type=number] {
            -moz-appearance: textfield;
            -webkit-appearance: none;
            margin: 0;
          }
        }
      }
    }
 }
</style>

<style lang="scss" scoped>

</style>
